const localModelsContent = {
    title: "Offline Mode: Using Local Models with Sortio",
    description: "Learn how to set up local AI models with Sortio using Ollama or LM Studio.",
    sections: [
      {
        id: "overview",
        title: "Choosing a Local Engine",
        content: `
          <p>Sortio can run AI models entirely on your own machine. In <strong>Settings &rarr; AI</strong>, the provider choices are <strong>Sortio Cloud</strong>, <strong>Offline Mode</strong>, and <strong>Bring Your Own Key</strong> (you can also switch from the AI selector in the title bar). When you choose <strong>Offline Mode</strong>, Sortio asks which engine you want to use:</p>

          <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
              <h4 class="font-medium text-[#1d1d1f]">Ollama</h4>
              <p class="text-sm text-[#86868b] mt-1">Command-line tool</p>
              <p class="mt-3 text-[#424245]">A lightweight, terminal-based way to pull and run models. Best if you're comfortable with a quick command or two and want the simplest possible setup.</p>
            </div>

            <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
              <h4 class="font-medium text-[#1d1d1f]">LM Studio</h4>
              <p class="text-sm text-[#86868b] mt-1">Desktop app with a model browser</p>
              <p class="mt-3 text-[#424245]">A full graphical app for finding, downloading, and loading open-weight models with no terminal required. Best if you prefer a point-and-click experience.</p>
            </div>
          </div>

          <div class="mt-8 bg-[#f5f5f7] border-l-4 border-[#06c] p-5 rounded-r-xl">
            <div class="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#06c] mt-0.5 mr-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h5 class="font-medium text-[#1d1d1f] mb-1">Which should I use?</h5>
                <p class="text-[#424245]">Both run models locally with identical privacy and offline behavior, both work with every Sortio feature, and with either engine nothing counts against your Sortio AI allowance. Choose <strong>Ollama</strong> if you like a simple command-line setup, or <strong>LM Studio</strong> if you'd rather browse and manage models in a desktop app. The Ollama sections below come first, followed by an LM Studio setup guide.</p>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "installation",
        title: "Installing Ollama",
        content: `
          <p>Sortio can use Ollama to run AI models locally on your computer. Follow these steps to install Ollama:</p>
          
          <div class="mt-6 border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4">
              <h4 class="font-medium text-[#1d1d1f]">Installation Options</h4>
            </div>
            
            <div class="p-5 space-y-6">
              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12.75l3 3m0 0l3-3m-3 3v-7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Download Ollama
                </h5>
                <p class="mt-1 text-[#424245]">Visit <a href="https://ollama.com/download" target="_blank" rel="noopener noreferrer" class="text-[#06c] hover:text-[#0077e6] transition-colors">ollama.com/download</a> and select your platform (macOS, Windows, or Linux)</p>
              </div>
              
              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15M9 12l3 3m0 0l3-3m-3 3V2.25" />
                  </svg>
                  Install and Run
                </h5>
                <p class="mt-1 text-[#424245]">Follow the installation instructions for your operating system</p>
              </div>
              
              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Verify Installation
                </h5>
                <p class="mt-1 text-[#424245]">Once installed, Ollama will run in the background ready to serve models</p>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "models",
        title: "Installing a Model",
        content: `
          <p>After installing Ollama, you'll need to download a model. Choose one based on your computer's capabilities:</p>
          
          <div class="space-y-5 mt-6">
            <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
              <h4 class="font-medium text-[#1d1d1f]">Recommended: Llama 3.3 or Deepseek-r1</h4>
              <p class="text-sm text-[#86868b] mt-1">Best performance for most file organization tasks</p>
              <div class="mt-3 bg-[#f5f5f7] rounded-lg overflow-hidden">
                <div class="bg-[#1d1d1f] py-2 px-4 text-xs text-white font-medium flex items-center">
                  <span>Terminal</span>
                  <div class="ml-2 flex space-x-1.5">
                    <div class="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                  </div>
                </div>
                <pre class="p-3 text-sm overflow-x-auto font-mono bg-gradient-to-r from-[#0d1117] to-[#1d1f21] text-white"><code><span class="text-[#81868d]">$</span> <span class="text-[#b392f0]">ollama</span> <span class="text-[#79b8ff]">pull</span> llama3.3</code></pre>
              </div>
            </div>
            
            <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
              <h4 class="font-medium text-[#1d1d1f]">Alternative: DeepSeek</h4>
              <p class="text-sm text-[#86868b] mt-1">Another excellent option for file organization</p>
              <div class="mt-3 bg-[#f5f5f7] rounded-lg overflow-hidden">
                <div class="bg-[#1d1d1f] py-2 px-4 text-xs text-white font-medium flex items-center">
                  <span>Terminal</span>
                  <div class="ml-2 flex space-x-1.5">
                    <div class="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                  </div>
                </div>
                <pre class="p-3 text-sm overflow-x-auto font-mono bg-gradient-to-r from-[#0d1117] to-[#1d1f21] text-white"><code><span class="text-[#81868d]">$</span> <span class="text-[#b392f0]">ollama</span> <span class="text-[#79b8ff]">pull</span> deepseek</code></pre>
              </div>
            </div>
            
            <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
              <h4 class="font-medium text-[#1d1d1f]">For Limited Resources</h4>
              <p class="text-sm text-[#86868b] mt-1">If your computer has less RAM or processing power</p>
              <div class="mt-3 bg-[#f5f5f7] rounded-lg overflow-hidden">
                <div class="bg-[#1d1d1f] py-2 px-4 text-xs text-white font-medium flex items-center">
                  <span>Terminal</span>
                  <div class="ml-2 flex space-x-1.5">
                    <div class="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                  </div>
                </div>
                <pre class="p-3 text-sm overflow-x-auto font-mono bg-gradient-to-r from-[#0d1117] to-[#1d1f21] text-white"><code><span class="text-[#81868d]">$</span> <span class="text-[#b392f0]">ollama</span> <span class="text-[#79b8ff]">pull</span> llama3.2</code></pre>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "using-with-sortio",
        title: "Using with Sortio",
        content: `
          <p>Once you've installed Ollama and a model, connecting it with Sortio is simple:</p>
          
          <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur flex flex-col">
              <div class="rounded-full bg-[#06c]/10 h-10 w-10 flex items-center justify-center mb-3">
                <span class="text-[#06c] font-medium">1</span>
              </div>
              <h4 class="font-medium text-[#1d1d1f]">Start Ollama</h4>
              <p class="text-sm text-[#86868b] mt-1 flex-grow">Ensure Ollama is running in the background on your computer</p>
            </div>
            
            <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur flex flex-col">
              <div class="rounded-full bg-[#06c]/10 h-10 w-10 flex items-center justify-center mb-3">
                <span class="text-[#06c] font-medium">2</span>
              </div>
              <h4 class="font-medium text-[#1d1d1f]">Open Settings</h4>
              <p class="text-sm text-[#86868b] mt-1 flex-grow">Go to Sortio's Settings menu and navigate to the AI Models section</p>
            </div>
            
            <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur flex flex-col">
              <div class="rounded-full bg-[#06c]/10 h-10 w-10 flex items-center justify-center mb-3">
                <span class="text-[#06c] font-medium">3</span>
              </div>
              <h4 class="font-medium text-[#1d1d1f]">Select Model</h4>
              <p class="text-sm text-[#86868b] mt-1 flex-grow">Choose your installed model from the dropdown menu</p>
            </div>
          </div>
          
          <div class="mt-8 bg-[#f5f5f7] border-l-4 border-[#06c] p-5 rounded-r-xl">
            <div class="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#06c] mt-0.5 mr-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h5 class="font-medium text-[#1d1d1f] mb-1">Enhanced Privacy</h5>
                <p class="text-[#424245]">Using local models ensures your files never leave your computer. All AI processing happens locally, providing complete privacy and offline functionality.</p>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "lm-studio",
        title: "Using LM Studio",
        content: `
          <p>LM Studio is a free desktop app that runs open-weight models locally and exposes an OpenAI-compatible local server. If you'd rather browse and manage models in a graphical app instead of the command line, choose <strong>LM Studio</strong> as your Offline Mode engine in Sortio. The privacy story is identical to Ollama: everything runs on your machine, nothing is sent to Sortio or any cloud, nothing counts against your Sortio allowance, and it works fully offline. All downstream features (sorting, Spaces, and Automations/rules) work the same way.</p>

          <div class="mt-6 border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-4">
              <h4 class="font-medium text-[#1d1d1f]">Setting Up LM Studio</h4>
            </div>

            <div class="p-5 space-y-6">
              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12.75l3 3m0 0l3-3m-3 3v-7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Install LM Studio
                </h5>
                <p class="mt-1 text-[#424245]">Download LM Studio from <a href="https://lmstudio.ai" target="_blank" rel="noopener noreferrer" class="text-[#06c] hover:text-[#0077e6] transition-colors">lmstudio.ai</a> and install it for your platform (macOS, Windows, or Linux).</p>
              </div>

              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15M9 12l3 3m0 0l3-3m-3 3V2.25" />
                  </svg>
                  Download and Load a Model
                </h5>
                <p class="mt-1 text-[#424245]">Use the in-app model browser to download a chat model, then load it so it's ready to serve. Any chat model loaded in LM Studio works with Sortio.</p>
              </div>

              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
                  </svg>
                  Start the Local Server
                </h5>
                <p class="mt-1 text-[#424245]">Open the <strong>Developer</strong> tab in LM Studio and start its local server. By default it runs at <a href="http://localhost:1234" target="_blank" rel="noopener noreferrer" class="text-[#06c] hover:text-[#0077e6] transition-colors">http://localhost:1234</a>.</p>
              </div>

              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Select LM Studio in Sortio
                </h5>
                <p class="mt-1 text-[#424245]">In Sortio go to <strong>Settings &rarr; AI &rarr; Offline Mode &rarr; LM Studio</strong>. Sortio auto-detects the running server and lists your loaded models. Pick the model you want to use.</p>
              </div>
            </div>
          </div>

          <div class="mt-6 border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur">
            <div class="border-b border-[#e0e0e5] p-5">
              <h4 class="font-medium text-[#1d1d1f]">Custom Server URL</h4>
            </div>
            <div class="p-5">
              <p class="text-[#424245]">If you run LM Studio's server on a non-default port, use the editable <strong>Server URL</strong> field on the LM Studio screen to point Sortio at the correct address. Most setups can leave it on the default <code class="px-1.5 py-0.5 rounded bg-[#f1f1f1]">http://localhost:1234</code>.</p>
            </div>
          </div>

          <div class="mt-8 bg-[#f5f5f7] border-l-4 border-[#06c] p-5 rounded-r-xl">
            <div class="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#06c] mt-0.5 mr-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h5 class="font-medium text-[#1d1d1f] mb-1">Tip: Picking a Model</h5>
                <p class="text-[#424245]">Smaller instruct models (for example Qwen2.5-7B-Instruct) tend to be faster and respond well for file organization. Large "reasoning" models are more capable but noticeably slower. Any chat model loaded in LM Studio will work, so you can experiment to find the right balance of speed and accuracy for your hardware.</p>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "performance",
        title: "Performance & Reliability",
        content: `
          <p>Sortio includes several features to ensure local model processing is fast and reliable:</p>

          <div class="mt-6 space-y-5">
            <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
              <div class="flex items-start">
                <div class="rounded-full bg-green-100 h-10 w-10 flex items-center justify-center mr-4 shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h4 class="font-medium text-[#1d1d1f]">Intelligent Batching</h4>
                  <p class="text-[#424245] mt-1">When sorting large numbers of files, Sortio automatically batches requests to your local model. This optimizes memory usage and ensures consistent performance even with hundreds of files.</p>
                </div>
              </div>
            </div>

            <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
              <div class="flex items-start">
                <div class="rounded-full bg-blue-100 h-10 w-10 flex items-center justify-center mr-4 shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h4 class="font-medium text-[#1d1d1f]">Automatic Retry Logic</h4>
                  <p class="text-[#424245] mt-1">If a request to Ollama fails due to a temporary issue (network hiccup, model busy, etc.), Sortio automatically retries with exponential backoff. This makes sorting more resilient without requiring manual intervention.</p>
                </div>
              </div>
            </div>

            <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
              <div class="flex items-start">
                <div class="rounded-full bg-purple-100 h-10 w-10 flex items-center justify-center mr-4 shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h4 class="font-medium text-[#1d1d1f]">Clear Error Messages</h4>
                  <p class="text-[#424245] mt-1">When something goes wrong, Sortio provides actionable error messages that help you understand the issue. Whether Ollama isn't running, the model isn't loaded, or there's a configuration problem, you'll know exactly what to fix.</p>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-8 bg-[#f5f5f7] border-l-4 border-[#06c] p-5 rounded-r-xl">
            <div class="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#06c] mt-0.5 mr-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h5 class="font-medium text-[#1d1d1f] mb-1">Tip: Model Performance</h5>
                <p class="text-[#424245]">For the best balance of speed and accuracy, we recommend llama3.3 or deepseek-r1 models. If you're sorting very large batches of files, smaller models like llama3.2 may complete faster while still providing good results.</p>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "troubleshooting",
        title: "Troubleshooting Offline Mode",
        content: `
          <p>If you're experiencing issues with local models, use these diagnostic commands to identify and solve the problem.</p>

          <div class="mt-6 border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur">
            <div class="border-b border-[#e0e0e5] p-5">
              <h4 class="font-medium text-[#1d1d1f]">Diagnostic Commands</h4>
              <p class="text-sm text-[#86868b] mt-1">Run these commands in your Terminal (macOS/Linux) or Command Prompt (Windows) to check if Ollama is working correctly.</p>
            </div>

            <div class="p-6 space-y-6">
              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center mb-2">
                  <div class="rounded-full bg-[#06c]/10 h-6 w-6 flex items-center justify-center mr-2">
                    <span class="text-[#06c] font-medium text-sm">1</span>
                  </div>
                  Check if Ollama is Running
                </h5>
                <div class="bg-[#f5f5f7] rounded-lg overflow-hidden">
                  <div class="bg-[#1d1d1f] py-2 px-4 text-xs text-white font-medium flex items-center">
                    <span>Terminal</span>
                    <div class="ml-2 flex space-x-1.5">
                      <div class="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                      <div class="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                      <div class="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                  <pre class="p-3 text-sm overflow-x-auto font-mono bg-gradient-to-r from-[#0d1117] to-[#1d1f21] text-white"><code><span class="text-[#81868d]">$</span> <span class="text-[#b392f0]">curl</span> http://localhost:11434/api/tags</code></pre>
                </div>
                <div class="mt-2 text-sm text-[#424245]">
                  <p><span class="font-medium text-green-600">Success:</span> Returns JSON with your installed models</p>
                  <p><span class="font-medium text-red-600">Failed:</span> "Connection refused" means Ollama isn't running</p>
                </div>
              </div>

              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center mb-2">
                  <div class="rounded-full bg-[#06c]/10 h-6 w-6 flex items-center justify-center mr-2">
                    <span class="text-[#06c] font-medium text-sm">2</span>
                  </div>
                  List Installed Models
                </h5>
                <div class="bg-[#f5f5f7] rounded-lg overflow-hidden">
                  <div class="bg-[#1d1d1f] py-2 px-4 text-xs text-white font-medium flex items-center">
                    <span>Terminal</span>
                    <div class="ml-2 flex space-x-1.5">
                      <div class="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                      <div class="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                      <div class="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                  <pre class="p-3 text-sm overflow-x-auto font-mono bg-gradient-to-r from-[#0d1117] to-[#1d1f21] text-white"><code><span class="text-[#81868d]">$</span> <span class="text-[#b392f0]">ollama</span> list</code></pre>
                </div>
                <div class="mt-2 text-sm text-[#424245]">
                  <p><span class="font-medium text-green-600">Success:</span> Shows table of installed models with sizes</p>
                  <p><span class="font-medium text-amber-600">Empty:</span> No models installed - run <code class="px-1.5 py-0.5 rounded bg-[#f1f1f1]">ollama pull llama3.2</code></p>
                </div>
              </div>

              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center mb-2">
                  <div class="rounded-full bg-[#06c]/10 h-6 w-6 flex items-center justify-center mr-2">
                    <span class="text-[#06c] font-medium text-sm">3</span>
                  </div>
                  Test Model Response
                </h5>
                <div class="bg-[#f5f5f7] rounded-lg overflow-hidden">
                  <div class="bg-[#1d1d1f] py-2 px-4 text-xs text-white font-medium flex items-center">
                    <span>Terminal</span>
                    <div class="ml-2 flex space-x-1.5">
                      <div class="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                      <div class="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                      <div class="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                  <pre class="p-3 text-sm overflow-x-auto font-mono bg-gradient-to-r from-[#0d1117] to-[#1d1f21] text-white"><code><span class="text-[#81868d]">$</span> <span class="text-[#b392f0]">ollama</span> run llama3.2 <span class="text-[#9ecbff]">"Say hello"</span></code></pre>
                </div>
                <div class="mt-2 text-sm text-[#424245]">
                  <p><span class="font-medium text-green-600">Success:</span> Model responds with a greeting</p>
                  <p><span class="font-medium text-red-600">Failed:</span> Model may need to be re-downloaded</p>
                </div>
              </div>

              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center mb-2">
                  <div class="rounded-full bg-[#06c]/10 h-6 w-6 flex items-center justify-center mr-2">
                    <span class="text-[#06c] font-medium text-sm">4</span>
                  </div>
                  Start Ollama (if not running)
                </h5>
                <div class="bg-[#f5f5f7] rounded-lg overflow-hidden">
                  <div class="bg-[#1d1d1f] py-2 px-4 text-xs text-white font-medium flex items-center">
                    <span>Terminal</span>
                    <div class="ml-2 flex space-x-1.5">
                      <div class="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                      <div class="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                      <div class="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                  <pre class="p-3 text-sm overflow-x-auto font-mono bg-gradient-to-r from-[#0d1117] to-[#1d1f21] text-white"><code><span class="text-[#81868d]">$</span> <span class="text-[#b392f0]">ollama</span> serve</code></pre>
                </div>
                <div class="mt-2 text-sm text-[#424245]">
                  <p>This starts the Ollama server. Keep this terminal window open, or use the Ollama app which runs in the background.</p>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-6 border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur">
            <div class="border-b border-[#e0e0e5] p-5">
              <h4 class="font-medium text-[#1d1d1f]">Hardware Requirements</h4>
            </div>
            <div class="p-5">
              <ul class="text-sm text-[#424245] space-y-2">
                <li><span class="font-medium">Memory:</span> 8GB RAM minimum, 16GB+ recommended for larger models</li>
                <li><span class="font-medium">GPU:</span> Dedicated GPU recommended for optimal performance</li>
                <li><span class="font-medium">Storage:</span> At least 10GB free space for model files</li>
                <li><span class="font-medium">CPU:</span> Modern multi-core processor (4+ cores recommended)</li>
              </ul>
              <div class="mt-4 pt-4 border-t border-[#e0e0e5]">
                <div class="text-sm font-medium text-[#1d1d1f] mb-2">Recommended Models by Hardware</div>
                <ul class="text-sm text-[#424245] space-y-1">
                  <li><span class="font-medium">8GB RAM:</span> llama3.2:1b, llama3.2:3b, deepseek-r1:1.5b</li>
                  <li><span class="font-medium">16GB RAM:</span> llama3.2, deepseek-r1:8b</li>
                  <li><span class="font-medium">24GB+ VRAM:</span> llama3.3:70b, deepseek-r1:70b</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div class="mt-8 space-y-6">
            <div class="bg-[#f5f5f7] border-l-4 border-amber-500 p-5 rounded-r-xl">
              <div class="flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-amber-500 mt-0.5 mr-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h5 class="font-medium text-[#1d1d1f] mb-1">Common Issues</h5>
                  <p class="text-[#424245]">The diagnostic tool above can help identify common issues. Here are additional troubleshooting steps for specific problems:</p>
                  <ul class="mt-3 space-y-2 text-[#424245]">
                    <li class="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span><strong>Ollama not starting:</strong> Check your system resources; Ollama requires at least 8GB of RAM to function properly.</span>
                    </li>
                    <li class="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span><strong>Very slow responses:</strong> Your model may be too large for your hardware. Try a smaller model like "llama3:8b" instead.</span>
                    </li>
                    <li class="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span><strong>Permission errors:</strong> Make sure Sortio has network permissions to access localhost in your system's security settings.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div class="bg-[#f8f8fa] rounded-xl p-6 border border-[#e0e0e5]">
              <h5 class="font-medium text-[#1d1d1f] mb-3">Still Having Issues?</h5>
              <p class="text-[#424245]">If you're still experiencing problems after running diagnostics and following the troubleshooting steps, contact our support team at <a href="mailto:marcus@getsortio.com" class="text-[#06c] hover:text-[#0077e6] font-medium">marcus@getsortio.com</a>.</p>
            </div>
          </div>
        `
      }
    ]
  };

  export default localModelsContent;